import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/i18n/LangContext";
import { smoothScrollTo } from "@/hooks/useSmoothScroll";

// FAQ entries — Hungarian + English. Keys are stable so JSON-LD stays aligned.
const FAQS = {
  hu: [
    {
      q: "Felhelyezhető a fólia a meglévő üvegemre?",
      a: "Igen — a PrivacyGlass™ Standard és Premium fóliákat helyszínen, professzionálisan laminálni tudjuk a már beépített egy- vagy kétrétegű üvegére, anélkül, hogy az üveget cserélni kellene.",
    },
    {
      q: "Mennyi fény jut át a fólián opál (matt) állapotban?",
      a: "Még opál állapotban is áthalad a természetes nappali fény (a fólia diffúz módon szórja), csak az átlátást szünteti meg. Tiszta állapotban a fényáteresztés akár 92%.",
    },
    {
      q: "Integrálható okosotthon-rendszerrel?",
      a: "Igen. Kínálunk távirányítós, falikapcsolós, valamint KNX, Loxone, Crestron és általános száraz-kontaktus vezérlőket. Külön kérésre Apple HomeKit / Google Home / Alexa is integrálható.",
    },
    {
      q: "Mennyi ideig tart egy telepítés?",
      a: "Egy tipikus lakossági fürdőszoba (~3 m²) telepítése fél nap. Nagyobb kereskedelmi projekteket előzetes egyeztetés után, általában 1–3 nap alatt zárunk le.",
    },
    {
      q: "Magyarországon kívül is vállaltok kivitelezést?",
      a: "Igen — a teljes EU-ban szállítunk és telepítünk. EU-n kívüli projekteket egyedi árajánlat alapján vállalunk.",
    },
    {
      q: "Mennyi a garancia?",
      a: "5 év a fóliára, 2 év a transzformátorra és az elektronikára. A garancia a normál működési ciklusokra (>10.000.000 váltás) érvényes.",
    },
    {
      q: "Alkalmas téli, magyarországi időjárásra?",
      a: "Igen. A PDLC réteg –20 °C és +60 °C között stabilan működik, kültéri vagy hidegfront elleni alkalmazás külön védőréteggel kérhető.",
    },
    {
      q: "Milyen áron érhetők el a termékek?",
      a: "A pontos ár a felület méretétől, a választott terméktől (Standard vagy Premium) és a kiegészítőktől függ. Egy gyors ajánlatkérést követően 24 órán belül egyedi árajánlatot küldünk e-mailben (PDF).",
    },
    {
      q: "Mi a különbség a Standard és Premium változatok között?",
      a: "Mindkét változat alapból opál (mattüveg-hatású), és áram alatt válik átlátszóvá. A Premium azonban gyorsabban kapcsol (<10 ms vs. ~50 ms), magasabb a fényáteresztése (90% vs. 88%) és erősebb az UV-/infravédelme (95% vs. 90%).",
    },
  ],
  en: [
    {
      q: "Can the film be retrofitted to my existing glass?",
      a: "Yes — PrivacyGlass™ Standard and Premium film can be professionally laminated on-site to your existing single- or double-glazed windows. There's no need to replace the glass itself.",
    },
    {
      q: "Does light still come through when the film is frosted?",
      a: "Yes — even in the frosted state, daylight passes through diffusely; only the line-of-sight visibility is blocked. In clear mode the film transmits up to 92% of light.",
    },
    {
      q: "Can it integrate with my smart home system?",
      a: "Yes. We deliver remote, wall-switch, KNX, Loxone, Crestron and dry-contact compatible controllers. Apple HomeKit / Google Home / Alexa bridges are available on request.",
    },
    {
      q: "How long does an installation take?",
      a: "A typical residential bathroom (~3 m²) takes a half-day. Larger commercial projects are scheduled across 1–3 days after a free on-site survey.",
    },
    {
      q: "Do you ship outside Hungary?",
      a: "Yes — we deliver and install across the EU. Projects outside the EU are quoted on a case-by-case basis.",
    },
    {
      q: "What's the warranty?",
      a: "5 years on the film, 2 years on the transformer and electronics. Coverage applies to normal operation (>10,000,000 switching cycles).",
    },
    {
      q: "Is it suitable for Hungarian winters / outdoor use?",
      a: "Yes — the PDLC layer operates reliably between –20 °C and +60 °C. Outdoor or cold-bridge installations are available with an additional protective layer on request.",
    },
    {
      q: "How much does it cost?",
      a: "Pricing depends on surface area, product variant (Standard or Premium) and add-ons. After a quick inquiry we send you a customised PDF quote within 24 hours.",
    },
    {
      q: "What's the difference between Standard and Premium?",
      a: "Both variants are frosted by default and turn clear when powered. Premium switches faster (<10 ms vs. ~50 ms), has higher light transmission (90% vs. 88%) and stronger UV/IR filtration (95% vs. 90%).",
    },
  ],
};

function ChevronDown({ open }) {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      className={`shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
      aria-hidden="true"
    >
      <path d="M5 7.5L10 12.5L15 7.5" stroke="#1D1D1F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function FAQPage() {
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const items = FAQS[lang] || FAQS.en;
  const [openIdx, setOpenIdx] = useState(0);

  const goToContact = (e) => {
    e.preventDefault();
    navigate("/");
    // After the route swap mounts the landing page, find #contact and scroll.
    // The Landing component scrolls to top on mount, so we delay slightly.
    setTimeout(() => {
      const el = document.getElementById("contact");
      if (el) smoothScrollTo(el, { offset: -64 });
    }, 100);
  };

  // Scroll-to-top + canonical override on mount.
  useEffect(() => {
    window.scrollTo(0, 0);
    // Override the static `<link rel="canonical">` from index.html so this
    // page's canonical URL is /faq, not the root. Restore on unmount.
    const canonical = document.querySelector('link[rel="canonical"]');
    const prev = canonical?.getAttribute("href");
    if (canonical) canonical.setAttribute("href", "https://privacyglass.hu/faq");
    return () => {
      if (canonical && prev) canonical.setAttribute("href", prev);
    };
  }, []);

  // Build FAQPage JSON-LD on the fly so it always matches the current language.
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: items.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };

  const titles = {
    hu: {
      pageTitle: "Gyakran Ismételt Kérdések · PrivacyGlass™",
      h1: "Gyakran Ismételt Kérdések",
      sub: "Minden, amit az okos PrivacyGlass™ üvegfóliáról és telepítéséről tudni érdemes — egy helyen.",
      backToHome: "Vissza a főoldalra",
      ctaTitle: "Nem találod a választ?",
      ctaText: "Írj nekünk és 24 órán belül egyedi ajánlatot küldünk.",
      ctaBtn: "Ajánlatkérés",
    },
    en: {
      pageTitle: "Frequently Asked Questions · PrivacyGlass™",
      h1: "Frequently Asked Questions",
      sub: "Everything worth knowing about PrivacyGlass™ smart privacy film & installation — in one place.",
      backToHome: "Back to home",
      ctaTitle: "Can't find your answer?",
      ctaText: "Drop us a line and we'll send a custom quote within 24 hours.",
      ctaBtn: "Get a quote",
    },
  }[lang] || {};

  return (
    <div data-testid="faq-page" className="min-h-screen bg-[#FAFAFA] text-[#1D1D1F]">
      <Helmet>
        <html lang={lang} />
        <title>{titles.pageTitle}</title>
        <meta
          name="description"
          content={lang === "hu"
            ? "Gyakran ismételt kérdések a PrivacyGlass™ okos üvegfóliáról: telepítés, vezérlés, garancia, ár, EU-szállítás. Egyedi árajánlat 24 órán belül."
            : "Frequently asked questions about PrivacyGlass™ smart privacy film: install, control, warranty, pricing, EU delivery. Custom quote within 24 hours."}
        />
        <meta property="og:title" content={titles.pageTitle} />
        <meta property="og:url" content="https://privacyglass.hu/faq" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <main className="max-w-3xl mx-auto px-6 md:px-10 pt-32 md:pt-40 pb-24">
        <a
          href="/"
          data-testid="faq-back-link"
          className="inline-flex items-center gap-1.5 text-sm font-body text-[#86868B] hover:text-[#1D1D1F] transition-colors mb-10"
        >
          <span aria-hidden="true">←</span>
          <span>{titles.backToHome}</span>
        </a>

        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.05] text-[#1D1D1F]">
          {titles.h1}
        </h1>
        <p className="mt-5 max-w-xl text-base md:text-lg font-body text-[#1D1D1F]/70 leading-relaxed">
          {titles.sub}
        </p>

        <div data-testid="faq-list" className="mt-14 divide-y divide-[#1D1D1F]/10 border-y border-[#1D1D1F]/10">
          {items.map(({ q, a }, idx) => {
            const isOpen = openIdx === idx;
            return (
              <div key={q} data-testid={`faq-item-${idx}`} className="py-2">
                <button
                  type="button"
                  data-testid={`faq-question-${idx}`}
                  onClick={() => setOpenIdx(isOpen ? -1 : idx)}
                  aria-expanded={isOpen}
                  className="w-full flex items-center justify-between gap-6 py-4 text-left group"
                >
                  <span className="font-body text-base md:text-lg text-[#1D1D1F] group-hover:text-black transition-colors">
                    {q}
                  </span>
                  <ChevronDown open={isOpen} />
                </button>
                <div
                  data-testid={`faq-answer-${idx}`}
                  className={`grid transition-[grid-template-rows,opacity] duration-300 ease-out ${
                    isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <div className="overflow-hidden">
                    <p className="pb-5 pr-8 font-body text-[15px] md:text-base text-[#1D1D1F]/70 leading-relaxed">
                      {a}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* CTA card */}
        <div
          data-testid="faq-cta"
          className="mt-16 rounded-3xl bg-[#1D1D1F] text-white px-8 md:px-12 py-10 md:py-12"
        >
          <h2 className="font-display text-2xl md:text-3xl font-medium tracking-tight">
            {titles.ctaTitle}
          </h2>
          <p className="mt-3 font-body text-white/70 max-w-md leading-relaxed">
            {titles.ctaText}
          </p>
          <a
            href="/#contact"
            onClick={goToContact}
            data-testid="faq-cta-btn"
            className="mt-7 inline-flex items-center justify-center bg-white text-[#1D1D1F] text-sm font-body font-medium rounded-full px-6 py-3 hover:bg-white/90 transition-colors"
          >
            {titles.ctaBtn}
          </a>
        </div>
      </main>
    </div>
  );
}
